import React from 'react';
import {Payment} from '../models/Payment';
import Card from '../../../Presentation/components/ui/Card';
import AppTable, {TableColumn} from '../../../Presentation/components/ui/AppTable';
import AppTableHeaderCell from '../../../Presentation/components/ui/AppTableHeaderCell';
import {AppText, AppBodyText, AppSectionTitle} from '../../../Presentation/components/ui/Typography';
import {formatCurrency} from '../../../Presentation/utils/numberUtils';
import DateTimeDisplay from '../../../Presentation/components/ui/DateTimeDisplay';
import {useTableExport, useExportConfig} from '../../../Presentation/hooks/useTableExport';
import ExportButton from '../../../Presentation/components/ui/ExportButton';
import {ExportFormat} from '../../../types/export.types';
import {ExportFormatters} from '../../../Presentation/utils/exportFormatters';

interface PaymentsTableProps {
  payments?: Payment[];
  loading?: boolean;
  onPageChange?: (page: number, pageSize?: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  paginatedData?: {
    data: Payment[];
    pagination?: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  } | null;
  exportable?: boolean;
}

const PaymentsTable: React.FC<PaymentsTableProps> = ({
  payments = [],
  loading = false,
  paginatedData,
  onPageChange,
  onPageSizeChange,
  exportable = false
}) => {
  // Ensure payments is always an array
  const safePayments = paginatedData?.data || payments || [];

  // Export configuration
  const {createConfig} = useExportConfig<Payment>();

  const exportConfig = createConfig([
    {key: 'id', title: 'معرف الدفع'},
    {key: 'paymentNumber', title: 'رقم الدفع'},
    {key: 'invoiceId', title: 'رقم الفاتورة'},
    {key: 'customerName', title: 'اسم العميل'},
    {key: 'marketerDeveloperName', title: 'اسم المسوق'},
    {key: 'totalAmount', title: 'المبلغ الإجمالي', formatter: ExportFormatters.currencySARExport},
    {
      key: 'paymentMethod',
      title: 'طريقة الدفع',
      formatter: (value) => value === 'نقداً' ? 'نقداً' : 'تحويل بنكي'
    },
    {key: 'paymentStatus', title: 'حالة الدفع'},
    {key: 'paymentDate', title: 'تاريخ الدفع', formatter: ExportFormatters.dateTime},
    {key: 'notes', title: 'ملاحظات'},
    {key: 'createdAt', title: 'تاريخ الإنشاء', formatter: ExportFormatters.dateTime}
  ], 'مدفوعات', 'تصدير سجلات المدفوعات', 'تصدير جميع سجلات المدفوعات مع التفاصيل المالية');

  // Export hook
  const {exportData, isExporting, canExport, recordCount} = useTableExport({
    data: safePayments,
    config: exportConfig
  });

  // Handle export
  const handleExport = async (format: ExportFormat) => {
    await exportData(format);
  };

  const handlePageChange = (newPage: number) => {
    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    if (onPageSizeChange) {
      onPageSizeChange(newPageSize);
    } else if (onPageChange) {
      // Fallback for backward compatibility
      onPageChange(1, newPageSize);
    }
  };

  const getPaymentMethodColor = (method: Payment['paymentMethod']) => {
    switch (method) {
      case 'نقداً':
        return 'bg-green-100 text-green-800 border border-green-200';
      case 'تحويل':
        return 'bg-brand-100 text-brand-800 border border-brand-200';
      default:
        return 'bg-gray-100 text-gray-800 border border-gray-200';
    }
  };

  const getPaymentMethodIcon = (method: Payment['paymentMethod']) => {
    switch (method) {
      case 'نقداً':
        return '💵';
      case 'تحويل':
        return '🏦';
      default:
        return '💰';
    }
  };

  const getPaymentStatusColor = (status?: string) => {
    switch (status) {
      case 'مدفوع بالكامل':
        return 'bg-green-100 text-green-800 border border-green-200';
      case 'موافق عليه':
        return 'bg-brand-100 text-brand-800 border border-brand-200';
      case 'في الانتظار':
        return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
      case 'مرفوض':
        return 'bg-red-100 text-red-800 border border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border border-gray-200';
    }
  };

  // Define table columns for AppTable
  const columns: TableColumn<Payment>[] = [
    {
      key: 'paymentNumber',
      title: <AppTableHeaderCell label="رقم المدفوعة" />,
      render: (payment) => (
        <AppText className="font-medium text-gray-900">
          {payment.paymentNumber || payment.id || 'غير محدد'}
        </AppText>
      )
    },
    {
      key: 'customerName',
      title: <AppTableHeaderCell label="اسم العميل" />,
      render: (payment) => (
        <AppText>{payment.customerName || 'غير محدد'}</AppText>
      )
    },
    {
      key: 'marketerDeveloperName',
      title: <AppTableHeaderCell label="اسم المسوق" />,
      render: (payment) => (
        <AppBodyText className="text-gray-600">
          {payment.marketerDeveloperName || '-'}
        </AppBodyText>
      )
    },
    {
      key: 'totalAmount',
      title: <AppTableHeaderCell label="المبلغ" />,
      render: (payment) => (
        <AppText className="font-semibold text-green-600">
          {formatCurrency(payment.totalAmount || 0)}
        </AppText>
      )
    },
    {
      key: 'paymentMethod',
      title: <AppTableHeaderCell label="طريقة الدفع" />,
      render: (payment) => (
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getPaymentMethodColor(payment.paymentMethod)}`}>
          <span>{getPaymentMethodIcon(payment.paymentMethod)}</span>
          {payment.paymentMethod}
        </span>
      )
    },
    {
      key: 'paymentStatus',
      title: <AppTableHeaderCell label="حالة الدفع" />,
      render: (payment) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(payment.paymentStatus)}`}>
          {payment.paymentStatus || 'غير محدد'}
        </span>
      )
    },
    {
      key: 'paymentDate',
      title: <AppTableHeaderCell label="تاريخ الدفع" />,
      render: (payment) => (
        payment.paymentDate ? <DateTimeDisplay dateString={payment.paymentDate} /> : <AppText className="text-gray-500">غير محدد</AppText>
      )
    },
    {
      key: 'notes',
      title: <AppTableHeaderCell label="الملاحظات" />,
      render: (payment) => (
        <AppBodyText className="max-w-xs truncate block text-gray-600">
          {payment.notes || '-'}
        </AppBodyText>
      )
    }
  ];


  return (
    <div className="w-full">
      <Card className="overflow-hidden">
        {/* Header */}
        <div className="p-6 bg-gray-50 border-b">
          <div className="flex justify-between items-center">
            <AppSectionTitle className="font-bold text-gray-900">
              سجل التحصيلات ({paginatedData?.pagination?.total || safePayments.length})
            </AppSectionTitle>
            <div className="flex items-center gap-4">
              <AppBodyText className="text-gray-600">
                جميع المدفوعات والتحصيلات المسجلة
              </AppBodyText>
              {/* Export Button */}
              {exportable && (
                <ExportButton
                  onExport={handleExport}
                  isExporting={isExporting}
                  canExport={canExport}
                  recordCount={recordCount}
                  showDropdown={true}
                />
              )}
            </div>
          </div>
        </div>

        {/* Table with horizontal scroll */}
        <div className="overflow-x-auto">
          <AppTable
            columns={columns}
            data={safePayments}
            rowKey={(record, index) => `payment-${record.id}-${index || 0}`}
            emptyText="لا توجد مدفوعات مسجلة"
            page={paginatedData?.pagination?.page}
            pageSize={paginatedData?.pagination?.limit}
            totalCount={paginatedData?.pagination?.total}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            showPageSizeSelector={true}
            showPaginationInfo={true}
            loading={loading}
            loadingRows={paginatedData?.pagination?.limit || 10}
            loadingMessage="جاري تحميل المدفوعات..."
          />
        </div>
      </Card>
    </div>
  );
};

export default PaymentsTable;
